import React, {
  ElementRef,
  FC,
  LazyExoticComponent,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Popover } from '@headlessui/react';
import * as Accordion from '@radix-ui/react-accordion';
import { VariantProps, cva } from 'class-variance-authority';
import { inflect } from 'inflection';
import type { IconType } from 'react-icons';
import { FiChevronDown } from 'react-icons/fi';
import type { To } from 'react-router-dom';
import { GenericIconProps } from 'ui/components/icon/Icon';
import { cn } from 'utils/helpers';
import { FaLock } from 'react-icons/fa';
import { Tooltip } from 'ui/components/common';
import { useSubscriptionContext } from 'utils/providers/Subscription';
import { PREMIUM_MESSAGE } from 'utils/constants';

const menuItem = cva(
  [
    'w-full',
    'flex gap-[20px] items-center text-secondary-light',
    'transition-all',
    'py-[9px] px-[15px]',
    'hover:text-primary-base',
    'max-h-[36px]',
    'data-[state=open]:font-semibold',
  ],
  {
    variants: {
      collapse: {
        true: 'justify-center',
      },
      menuType: {
        side: ['text-title', 'data-[state=open]:text-title', '!p-3'],
        default: ['data-[state=open]:text-primary-base'],
      },
    },
  },
);

export type MenuItemProps = VariantProps<typeof menuItem> &
  Accordion.AccordionItemProps & {
    label?: string;
    menuType?: 'default' | 'side';
    value: string;
    iconLeft?:
      | IconType
      | FC<GenericIconProps>
      | LazyExoticComponent<FC<GenericIconProps>>;
    onClick?: (value: string) => void;
    onSubClick?: (...args: any[]) => void;
    onKeyUp?: React.KeyboardEventHandler<HTMLLIElement>;
    subMenu?: {
      path?: string;
      label?: string;
      disabled?: boolean;
      className?: string | string[];
    }[];
    isActive?: boolean;
    disabled?: boolean;
    subActive?: string;
    expandSubMenu?: boolean;
    subTriggerByClick?: boolean;
    collapse?: boolean;
    isLocked?: boolean;
  };

const MenuItem: React.FC<MenuItemProps> = ({
  label,
  menuType,
  iconLeft: IconLeft,
  onClick,
  onKeyUp,
  subMenu,
  onSubClick,
  isActive,
  value,
  subActive,
  expandSubMenu,
  subTriggerByClick,
  collapse,
  isLocked,
}) => {
  const {
    state: { isSubscribe: _isSubscribe },
  } = useSubscriptionContext();
  const [subMenuOpen, setSubMenuOpen] = useState(expandSubMenu);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleClick: React.ComponentProps<
    typeof Popover.Button
  >['onClick'] = () => {
    onClick?.(value);
    setSubMenuOpen(!subMenuOpen);
  };

  const ref = useRef<ElementRef<'button'>>(null);

  const mouseBehavior = useMemo(() => {
    if (!subTriggerByClick && subMenu?.length)
      return {
        onMouseEnter: () => {
          ref.current?.click();
        },
      };
    return {};
  }, [subMenu?.length, subTriggerByClick]);

  const isSubMenuActive = subMenu && subMenu.find((s) => s.path === subActive);

  const locked = useMemo(
    () => isLocked && !_isSubscribe,
    [isLocked, _isSubscribe],
  );

  const renderItem = useMemo(
    () => (
      <Accordion.Item
        {...mouseBehavior}
        value={value}
        className={cn(
          !collapse && 'overflow-hidden',
          'relative w-full transition-all group ',
        )}
        disabled={locked}
      >
        <Accordion.Trigger
          onClick={handleClick}
          ref={ref}
          data-tut={`TOUR_${label?.toUpperCase()}_MENU`}
          className={cn(
            menuItem({ collapse, menuType }),
            'group/trigger',
            'relative',
            (isActive || !!isSubMenuActive) &&
              menuType === 'side' &&
              'bg-fill-primary rounded-md',
            (isActive || !!isSubMenuActive) &&
              menuType !== 'side' &&
              'text-primary-base',
          )}
        >
          {menuType !== 'side' && (isSubMenuActive || isActive) && (
            <div
              className={cn(
                'absolute inset-y-0 left-0 h-full w-[3px] bg-primary-base data-[state=open]:hidden',
              )}
            />
          )}
          {menuType !== 'side' && IconLeft && (
            <IconLeft
              className={cn(
                'w-[18px] h-[18px]',
                'text-secondary-light',
                isActive && 'text-primary-base',
                'group-hover:text-primary-base',
                locked && ['group-hover:text-fill-link', 'text-fill-link'],
                'data-[state=open]:!text-text-fill-link',
              )}
            />
          )}
          {!collapse && (
            <span
              className={cn(
                {
                  'font-semibold': isActive || isSubMenuActive,
                },
                'text-sm',
                locked && ['group-hover:text-fill-link', 'text-fill-link'],
              )}
            >
              {label}
            </span>
          )}
          {!collapse && (
            <div className="flex justify-end flex-1">
              {locked ? (
                <FaLock className={cn('text-xs', 'm-1', 'text-fill-link')} />
              ) : (
                !!subMenu?.length && (
                  <FiChevronDown
                    className={cn(
                      'transition-all w-5 h-5',
                      'group-aria-expanded/trigger:rotate-180',
                    )}
                  />
                )
              )}
            </div>
          )}
        </Accordion.Trigger>
        {!!subMenu?.length && !collapse && (
          <Accordion.Content
            className={cn(
              'px-[15px] pb-2 data-[state=open]:animate-slideDown',
              'max-h-[250px] overflow-y-scroll',
              'data-[state=closed]:animate-slideUp',
              'scrollbar-none',
            )}
          >
            {subMenu?.map((sub) => (
              <div
                tabIndex={0}
                data-tut={`TOUR_${label}_${inflect(sub.label ?? '', 1)}_SUBMENU`.toUpperCase()}
                aria-disabled={sub.disabled}
                className={cn(
                  'group/listItem text-secondary-light cursor-pointer',
                  menuType !== 'side' && 'aria-disabled:text-secondary-base',
                  menuType === 'side' &&
                    'text-content aria-disabled:text-disabled',
                  'aria-disabled:pointer-events-none',
                  'hover:text-secondary-base',
                )}
                key={sub.path}
                role="button"
                onClick={() => onSubClick?.(sub.path, value)}
                onKeyUp={onKeyUp}
              >
                <div
                  className={cn(
                    'flex items-center border border-transparent',
                    { 'pl-[38px]': !collapse && menuType === 'default' },
                    { 'pl-2': menuType === 'side' },
                    'py-1.5',
                    { 'px-3': collapse },
                    subActive === sub.path &&
                      menuType !== 'side' &&
                      'bg-fill-link text-fill-tertiary',
                    subActive === sub.path &&
                      menuType === 'side' &&
                      'text-title',
                    subActive === sub.path && 'rounded-md font-semibold',
                    subActive !== sub.path &&
                      'group-hover/listItem:text-primary-base',
                  )}
                >
                  <span className={cn('text-sm leading-[22px]')}>
                    {sub.label}
                  </span>
                </div>
              </div>
            ))}
          </Accordion.Content>
        )}
        {!!subMenu?.length && !locked && collapse && (
          <Accordion.Content forceMount>
            <div
              className={cn(
                'py-2 space-y-1 overflow-hidden',
                'transition-all',
                'w-[230px] rounded-md bg-fill-quarternary',
                'invisible opacity-0 group-hover:visible group-hover:opacity-100',
                'absolute bg-fill-quarternary rounded-md left-full top-0',
                'ml-1 w-[190px]',
              )}
            >
              {subMenu?.map((sub) => (
                <div
                  data-tut={`TOUR_${label}_${inflect(sub.label ?? '', 1)}_SUBMENU`.toUpperCase()}
                  aria-disabled={sub.disabled}
                  className={cn(
                    'group/listItem px-[15px] text-secondary-light cursor-pointer',
                    'aria-disabled:text-secondary-base',
                    'aria-disabled:pointer-events-none',
                    'hover:text-secondary-base',
                  )}
                  key={sub.path}
                  role="menuitem"
                  onClick={() => onSubClick?.(sub.path, value)}
                  onKeyUp={onKeyUp}
                >
                  <div
                    className={cn(
                      'max-h-[36px] py-[7px]',
                      'px-3',
                      subActive === sub.path &&
                        'bg-fill-link rounded-md text-fill-tertiary font-semibold',
                      'group-hover/listItem:text-fill-tertiary',
                      subActive !== sub.path &&
                        'group-hover/listItem:text-primary-base',
                    )}
                  >
                    <span
                      className={cn('leading-[22px] text-sm tracking-[0.56px ')}
                    >
                      {sub.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Accordion.Content>
        )}
      </Accordion.Item>
    ),
    [
      IconLeft,
      collapse,
      handleClick,
      isActive,
      locked,
      isSubMenuActive,
      label,
      menuType,
      mouseBehavior,
      onKeyUp,
      onSubClick,
      subActive,
      subMenu,
      value,
    ],
  );

  if (locked)
    return (
      <Tooltip
        description={PREMIUM_MESSAGE}
        placement="right"
        className="w-full"
        title={null}
        theme="dark"
        tooltipContainerClassName="w-[5000px]"
      >
        {renderItem}
      </Tooltip>
    );

  return renderItem;
};

const menu = cva(
  [
    'transition-all',
    'bg-fill-quarternary',
    'overflow-hidden',
    'max-w-[230px] w-[230px]',
  ],
  {
    variants: {
      collapse: {
        true: ['max-w-[58px] overflow-visible'],
      },
      menuType: {
        side: ['bg-fill-tertiary', 'rounded-md'],
        default: [],
      },
    },
    defaultVariants: {
      menuType: 'default',
    },
  },
);

export type MenuProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> &
  VariantProps<typeof menu> & {
    className?: React.HTMLAttributes<HTMLDivElement>['className'];
    menuType?: 'default' | 'side';
    collapse?: boolean;
    defaultActive?: string;
    defaultSubActive?: string;
    defaultPath?: string;
    isSideNav?: boolean;
    items: {
      path: string;
      label?: string;
      iconLeft?:
        | IconType
        | FC<GenericIconProps>
        | LazyExoticComponent<FC<GenericIconProps>>;
      className?: React.ComponentProps<typeof MenuItem>['className'];
      disabled?: boolean;
      atFooter?: boolean;
      subMenu?: {
        path?: string;
        label?: string;
        disabled?: boolean;
        className?: string | string[];
      }[];
      isLocked?: boolean;
    }[];
    onChange: (val: To) => void;
    subTriggerByClick?: boolean;
  };

const Menu: React.FC<MenuProps> = ({
  items,
  collapse,
  defaultActive,
  defaultSubActive,
  defaultPath,
  menuType: menuType = 'default',
  onChange,
  subTriggerByClick = false,
  ...props
}) => {
  const defaultPathUrl =
    defaultPath !== ''
      ? defaultActive?.replace(defaultPath || '', '').split('/')[0]
      : defaultActive;
  const [active, setActive] = useState(defaultPathUrl);
  const [subActive, setSubActive] = useState(defaultSubActive);
  const handleClick: (val: string) => void = (val: string) => {
    setActive(val);
    setSubActive(undefined);
    onChange(val);
  };

  const handleSubClick: MenuItemProps['onSubClick'] = (subVal, val) => {
    setActive(val);
    setSubActive(subVal);
    onChange(subVal);
  };

  const headerItems = items.filter(({ atFooter }) => !atFooter);
  const footerItems = items.filter(({ atFooter }) => atFooter);

  return (
    <div className={cn(menu({ collapse, menuType: menuType }))} {...props}>
      <Accordion.Root
        type="single"
        collapsible
        className="flex flex-col justify-between h-full"
      >
        <div className={cn(menuType === 'side' && 'space-y-[2px]')}>
          {headerItems.map((item) => (
            <MenuItem
              menuType={menuType}
              collapse={collapse}
              key={item.path}
              isActive={active === item.path}
              {...(!item.subMenu?.length && {
                onClick: () => handleClick(item.path),
              })}
              onSubClick={handleSubClick}
              subActive={subActive}
              subTriggerByClick={subTriggerByClick}
              value={item.path}
              iconLeft={item.iconLeft}
              label={item.label}
              subMenu={item.subMenu}
              className={item.className}
              isLocked={item.isLocked}
            />
          ))}
        </div>
        {!!footerItems.length && (
          <div className="py-3 space-y-3">
            <div className="border-[0.5px] border-t-[#555F6D]" />
            {footerItems.map((item) => (
              <MenuItem
                menuType={menuType}
                collapse={collapse}
                key={item.path}
                isActive={active === item.path}
                onClick={() => handleClick(item.path)}
                onSubClick={handleSubClick}
                subActive={subActive}
                subTriggerByClick={subTriggerByClick}
                value={item.path}
                iconLeft={item.iconLeft}
                label={item.label}
                subMenu={item.subMenu}
                className={item.className}
              />
            ))}
          </div>
        )}
      </Accordion.Root>
    </div>
  );
};

export default Menu;
